import { Skeleton } from "@mui/material";

import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { AssociationType } from "@/shared/domain/association";

import { useWorkspaceEvent } from "../WorkspaceCtx/useWorkspaceEvents";

import styles from "./EventScope.module.css";
import {
  AssociatedDataset,
  AssociatedFile,
  AssociatedMessagePath,
  AssociatedTopic,
  SessionEventTopic,
} from "./scopes";

interface EventScopeProps {
  eventId: string;
}

export function EventScope({ eventId }: EventScopeProps) {
  const eventQuery = useWorkspaceEvent(eventId);

  if (eventQuery.isPending) {
    return (
      <ScopeSection>
        <Skeleton height="24px" variant="rounded" />
      </ScopeSection>
    );
  }

  if (eventQuery.isError) {
    return (
      <ScopeSection>
        <RobotoTypography>Failed to load event scope</RobotoTypography>
      </ScopeSection>
    );
  }

  const event = eventQuery.data;

  // A session event is about topics of its session rather than associated
  // with datasets, files, topics or message paths.
  if (event.session_id !== undefined) {
    const topics = event.topics ?? [];
    return (
      <ScopeSection>
        <div className={styles.associationList}>
          {topics.length === 0 ? (
            <RobotoTypography component={"span"}>
              All of the session&apos;s data
            </RobotoTypography>
          ) : (
            topics.map((topic) => (
              <SessionEventTopic
                key={topic.topic_id}
                event={event}
                topic={topic}
              />
            ))
          )}
        </div>
      </ScopeSection>
    );
  }

  return (
    <ScopeSection>
      <div className={styles.associationList}>
        {event.associations.map((association) => {
          switch (association.association_type) {
            case AssociationType.Dataset:
              return (
                <AssociatedDataset
                  key={association.association_id}
                  datasetId={association.association_id}
                />
              );
            case AssociationType.File:
              return (
                <AssociatedFile
                  key={association.association_id}
                  event={event}
                  fileId={association.association_id}
                />
              );
            case AssociationType.MessagePath:
              return (
                <AssociatedMessagePath
                  key={association.association_id}
                  event={event}
                  messagePathId={association.association_id}
                />
              );
            case AssociationType.Topic:
              return (
                <AssociatedTopic
                  key={association.association_id}
                  event={event}
                  topicId={association.association_id}
                />
              );
            default:
              return <span key={association.association_id}>Unknown</span>;
          }
        })}
      </div>
    </ScopeSection>
  );
}

function ScopeSection({ children }: { children: React.ReactNode }) {
  return (
    <section>
      <RobotoTypography component={"h3"} className={styles.sectionTitle}>
        Scope
      </RobotoTypography>
      {children}
    </section>
  );
}
